
import { Component, Prop, Vue } from 'bits/vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'

export enum OzMenuRowsSizePreset {
  H44px = 'H44px',
  H48px = 'H48px',
  H56px = 'H56px',
}

export enum OzMenuRowsFocusStyle {
  None = 'None',
  Bubble100 = 'Bubble100',
  Bubble200 = 'Bubble200',
  Grey100 = 'Grey100',
  HighlightText = 'HighlightText',
  ButtonDefault = 'ButtonDefault',
}

export enum OzMenuRowsRowStyle {
  Default = 'Default',
  Primary = 'Primary',
  Secondary = 'Secondary',
  Danger = 'Danger',
  Disabled = 'Disabled',
  Highlighted = 'Highlighted',
  Focused = 'Focused',
}

export enum OzMenuRowsHrefMode {
  Default = 'Default',
  DisplayOnly = 'DisplayOnly',
  HistoryPushState = 'HistoryPushState',
}

export interface OzMenuRowsRow<EventPayload = any> {
  href?: string
  hrefMode?: OzMenuRowsHrefMode
  isDisabled?: boolean
  ref?: string
  testId?: string
  analyticsId?: string
  eventPayload?: EventPayload
  icon?: string
  text?: string
  htmlText?: string
  textStyleClass?: string
  textTruncated?: boolean
  rightIcon?: string | null
  rightIconSize?: number
  rightLabel?: string
  rightIconAriaLabel?: string
  rightIconHighlighted?: boolean
  rowStyle?: OzMenuRowsRowStyle
  // Accessibility
  ariaChecked?: string
  ariaLabel?: string
  role?: 'menuitem' | 'menuitemradio' | 'menuitemcheckbox'
  rowClasses?: string
}

@Component({ components: { OzIcon, OzDivider } })
export default class OzMenuRows extends Vue {
  FocusStyle = OzMenuRowsFocusStyle
  OzDividerColorScheme = OzDividerColorScheme
  RowStyle = OzMenuRowsRowStyle
  SizePreset = OzMenuRowsSizePreset

  /**
   * Dark mode
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Size of the rows.
   *
   * All sizes have at least a 44px by 44px touch area and are thus mobile compatiple.
   */
  @Prop({ default: OzMenuRowsSizePreset.H48px }) sizePreset!: OzMenuRowsSizePreset

  /**
   * Focus style.
   */
  @Prop({ required: true }) focusStyle!: OzMenuRowsFocusStyle

  /**
   * Set to true if you want to concatenate multiple `<OzMenuRows>`s.
   */
  @Prop({ default: false }) xLastRowDivider!: boolean

  /**
   * Use this to set the same icon for all the rows.
   */
  @Prop({ default: () => [] }) commonSettings!: OzMenuRowsRow

  /**
   * Describes the rows
   */
  @Prop({ required: true }) rows!: OzMenuRowsRow[]

  /**
   * Use this to decide how the `activeRow` should be scrolled into view.
   */
  @Prop({ default: false }) isPhone?: boolean

  /**
   * Be scrolled into view to be visible to the user.
   *
   * It has to be matched with `OzMenuRowsRow.ref`.
   */
  @Prop({ default: '' }) activeRow?: string

  /**
   * List wrapper role for accessibility
   */
  @Prop() accessibilityRole?: 'menu' | 'group'

  get iconSize(): number {
    return {
      [OzMenuRowsSizePreset.H44px]: 16,
      [OzMenuRowsSizePreset.H48px]: 24,
      [OzMenuRowsSizePreset.H56px]: 24,
    }[this.sizePreset]
  }

  /**
   * Merge commonSettings into the rows
   */
  get rowsData(): OzMenuRowsRow[] {
    return this.rows.map((row) => ({ rowStyle: OzMenuRowsRowStyle.Default, ...this.commonSettings, ...row }))
  }

  get xLeftIcon(): boolean {
    return this.rowsData.some((row) => row.icon)
  }

  get textHighlightStyles(): any {
    return [
      {
        'hover-hover:group-hover:text-grape-500 hover-hover:dark:group-hover:text-canary-500': this.darkMode === 'auto',
        'hover-hover:group-hover:text-grape-500': this.darkMode === false,
        'hover-hover:group-hover:text-canary-500': this.darkMode === true,
      },
      {
        'group-focus-visible:text-grape-500 dark:group-focus-visible:text-canary-500': this.darkMode === 'auto',
        'group-focus-visible:text-grape-500': this.darkMode === false,
        'group-focus-visible:text-canary-500': this.darkMode === true,
      },
      {
        'group-active:text-grape-500 dark:group-active:text-canary-500': this.darkMode === 'auto',
        'group-active:text-grape-500': this.darkMode === false,
        'group-active:text-canary-500': this.darkMode === true,
      },
    ]
  }

  getAccessibilityAttrs(row: OzMenuRowsRow): Record<string, string | undefined> {
    return {
      'aria-checked': row.ariaChecked,
      'aria-label': row.ariaLabel,
      role: row.role || (this.accessibilityRole ? 'menuitem' : undefined),
    }
  }

  selectRow(event: Event, row: OzMenuRowsRow): void {
    // if row is disabled do not emit event
    if (row.isDisabled) return

    if (row.hrefMode !== OzMenuRowsHrefMode.Default) event.preventDefault()
    if (row.hrefMode === OzMenuRowsHrefMode.HistoryPushState) {
      // Store the url in the history state so you can read it later
      window?.history?.pushState({ url: row.href }, '', row.href)
    }

    /**
     * Emitted when a menu item is selected.
     */
    this.$emit('select-row', event, row.eventPayload)
  }

  shouldShowRowFocusRing(row: OzMenuRowsRow): boolean {
    return !row.rightLabel
  }

  mounted(): void {
    if (this.isPhone) {
      // Prevent the UI from laggy on mobile when scrolling into view that contains the `activeRow`
      setTimeout(() => {
        this.scrollActiveRowIntoView()
      }, 500)
    } else {
      this.scrollActiveRowIntoView()
    }
  }

  scrollActiveRowIntoView(): void {
    if (!this.activeRow) return
    const element = (this.$refs[this.activeRow] as HTMLDivElement[])?.[0]
    element?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' })
  }
}
